import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { PrimeIcons } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCurrentUser } from "../../../../common/api/useCurrentUser";
import { useUpdateCurrentUser } from "../../../../common/api/useUpdateCurrentUser";
import {
    Button,
    BUTTON_SEVERITIES,
    Dropdown,
    MESSAGE_TYPES,
    Modal,
} from "../../../../common/components";
import { PermissionsGate } from "../../../../common/components/PermissionsGate";
import { DEFAULT_LOCALE, THEMES } from "../../../../common/constants";
import { showToast } from "../../../../common/services/toast";
import { useUIStore } from "../../../../common/store/useUIStore";
import { LanguageType } from "../../../../common/types";
import { confirmDialog } from "../../../../common/utils/dialog.utils";
import {
    CompaniesAndRoles,
    CompanyRoleMeta,
} from "../../../users/components/CompaniesAndRoles";

interface ProfileModalProps {
    isOpen?: boolean;
    onClose: () => void;
}

const ProfileModal = (props: ProfileModalProps) => {
    const { isOpen, onClose } = props;

    const { t, i18n } = useTranslation([
        "common",
        "profile",
        "language",
        "toast",
    ]);
    const router = useRouter();
    const { user, mutateCurrentUser } = useCurrentUser();

    const [selectedLanguage, setSelectedLanguage] =
        useState<LanguageType>(DEFAULT_LOCALE);
    const [username, setUsername] = useState(user?.username);
    const [companyIdsToRemove, setCompanyIdsToRemove] = useState<string[]>([]);

    const { theme, setTheme } = useUIStore();

    const { triggerUpdateCurrentUser, isLoadingUpdateCurrentUser } =
        useUpdateCurrentUser({
            onSuccess: () => {
                const nonLanguageChanged =
                    username !== user?.username || !isEmpty(companyIdsToRemove);

                onClose();
                if (nonLanguageChanged) {
                    showToast({
                        severity: MESSAGE_TYPES.SUCCESS,
                        summary: t("toast:profile.edited.title"),
                        detail: t("toast:profile.edited.description"),
                    });
                }
                mutateCurrentUser({
                    ...user,
                    username,
                    languageCode: selectedLanguage,
                });
            },
        });

    useEffect(() => {
        if (user?.languageCode) {
            setSelectedLanguage(user.languageCode);
        }
    }, [user?.languageCode]);

    useEffect(() => {
        if (isOpen) {
            setUsername(user?.username);
            setCompanyIdsToRemove([]);
        }
    }, [isOpen, user?.username]);

    const getLanguageOption = (languageCode: string) => ({
        name: t(`language:languages.${languageCode}`),
        id: languageCode,
    });

    const languageOptions = router.locales?.map(getLanguageOption);

    const isDataChanged = useMemo(() => {
        if (username !== user?.username) {
            return true;
        }

        if (selectedLanguage !== user?.languageCode) {
            return true;
        }

        if (!isEmpty(companyIdsToRemove)) {
            return true;
        }

        return false;
    }, [
        companyIdsToRemove,
        selectedLanguage,
        user?.languageCode,
        user?.username,
        username,
    ]);

    const onClickSave = () => {
        if (!isDataChanged) {
            return;
        }
        triggerUpdateCurrentUser({
            languageCode: selectedLanguage,
            username,
            removeFromCompanyIds: companyIdsToRemove,
        });
    };

    const handleOnUsernameChange = (e: ChangeEvent<HTMLInputElement>) =>
        setUsername(e.target.value);

    const ActionBody = (data: CompanyRoleMeta) => {
        if (
            !user?.companies ||
            user.companies.length - companyIdsToRemove.length <= 1
        ) {
            return null;
        }

        const handleOnRemove = () =>
            confirmDialog({
                header: t("profile:removeCompany"),
                message: (
                    <Trans
                        i18nKey="profile:removeCompanyQuestion"
                        values={{
                            companyName: "name" in data ? data.name : "",
                        }}
                    />
                ),
                acceptLabel: t("profile:removeCompany") || "",
                rejectLabel: t("common:cancel") || "",
                acceptClassName: "p-button-danger",
                rejectClassName: "p-button-secondary p-button-text",
                contentClassName: "*:m-0!",
                accept: () =>
                    setCompanyIdsToRemove([...companyIdsToRemove, data.id!]),
            });

        return (
            <Button
                icon={PrimeIcons.TIMES}
                rounded
                text
                severity={BUTTON_SEVERITIES.NEUTRAL}
                onClick={handleOnRemove}
            />
        );
    };

    if (!i18n.isInitialized) {
        return null;
    }

    return (
        <Modal
            title={i18n.exists("profile:title") ? t("profile:title") : ""}
            isOpen={isOpen}
            hasOkButton
            hasCancelButton
            isOkButtonDisabled={isLoadingUpdateCurrentUser || !isDataChanged}
            onClickCancel={onClose}
            onClickClose={onClose}
            onClickOk={onClickSave}
            cancelText={t("common:cancel")}
            okText={t("common:save")}
        >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div className="col-span-2">
                    <label
                        className="block text-xs text-text-500/60"
                        htmlFor="email"
                    >
                        {i18n.exists("profile:email") ? t("profile:email") : ""}
                    </label>
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-envelope text-primary-500" />
                        <InputText
                            className="w-full"
                            disabled
                            id="email"
                            value={user?.email}
                        />
                    </IconField>
                </div>
                <div className="col-span-2">
                    <label
                        className="block text-xs text-text-500/60"
                        htmlFor="username"
                    >
                        {t("common:name")}
                    </label>
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-user text-primary-500" />
                        <InputText
                            className="w-full"
                            id="username"
                            value={username}
                            onChange={handleOnUsernameChange}
                        />
                    </IconField>
                </div>
                <div className="col-span-1">
                    <label className="block text-xs text-text-500/60">
                        {`${t("language:language")}*`}
                    </label>
                    <Dropdown
                        className="w-full"
                        value={
                            selectedLanguage &&
                            getLanguageOption(selectedLanguage)
                        }
                        onChange={(e) => setSelectedLanguage(e.value.id)}
                        options={languageOptions}
                        optionLabel="name"
                        placeholder={t("language:selectLanguage") || ""}
                    />
                </div>
                <PermissionsGate>
                    <div className="col-span-2">
                        <label className="block text-xs text-text-500/60">
                            {`${t("common:theme")}`}
                        </label>
                        <div className="flex items-center gap-1.5">
                            <i className="pi pi-moon" />
                            <InputSwitch
                                checked={theme === THEMES.LIGHT}
                                onChange={() => {
                                    const newTheme =
                                        theme === THEMES.LIGHT
                                            ? THEMES.DARK
                                            : THEMES.LIGHT;
                                    document.documentElement.setAttribute(
                                        "data-theme",
                                        newTheme
                                    );
                                    setTheme(newTheme);
                                }}
                            />
                            <i className="pi pi-sun" />
                        </div>
                    </div>
                </PermissionsGate>
            </div>
            <div>
                <CompaniesAndRoles
                    globalRoles={user?.globalRoles}
                    userCompanies={user?.companies}
                    companyIdsToRemove={companyIdsToRemove}
                    ActionsBody={ActionBody}
                />
            </div>
        </Modal>
    );
};

export default ProfileModal;
